'use client';

import React, { useState } from 'react';

type FaqItem = {
  question: string;
  answer: string;
};

type Props = {
  faqs: FaqItem[];
};

const FaqSection = ({ faqs }: Props) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  if (!faqs?.length) return null;

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <section className='mb-[30px] mt-[30px] rounded-[20px] border border-[#dceaf8] bg-[#f2f9ff] p-[20px] dark:border-[#334957] dark:bg-[#102636] lg:p-[35px]'>
        <h2 className='mb-[20px] text-[22px] font-semibold text-[#1c2534] dark:text-white'>
          Frequently Asked Questions
        </h2>
        <div className='flex flex-col gap-[12px]'>
          {faqs.map((item, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={`faq:${index}`}
                className='overflow-hidden rounded-[12px] border border-[#dceaf8] bg-white dark:border-[#334957] dark:bg-[#1a3344]'
              >
                <button
                  className='flex w-full items-center justify-between gap-[10px] p-[16px] text-left'
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                >
                  <span className='text-[15px] font-medium text-[#1c2534] dark:text-white lg:text-[17px]'>
                    {item.question}
                  </span>
                  <span
                    className='shrink-0 text-[22px] font-light text-[#00b4b9] transition-transform duration-200'
                    style={{
                      transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  }`}
                >
                  <div className='overflow-hidden'>
                    <p className='border-t border-[#dceaf8] p-[16px] text-[14px] leading-[1.7] text-[#4a5568] dark:border-[#334957] dark:text-[#cbd5e0] lg:text-[16px]'>
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
};

export default FaqSection;
